import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PasswordInput from '../components/ui/password-input';
import PasswordStrengthMeter from '../components/ui/password-strength-meter';
import { Label } from '../components/ui/label';
import { Flower } from 'iconoir-react';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const data = await register(email, password, name);
      navigate(data?.is_paid ? '/dashboard' : '/paywall');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex relative"
      data-testid="register-page"
      style={{ background: '#FFFFFF' }}
    >
      <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-moss/10 blur-3xl pointer-events-none hidden" />
      <div className="absolute bottom-20 right-1/3 w-80 h-80 rounded-full bg-ochre/15 blur-3xl pointer-events-none hidden" />
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1646925910552-513704cf0f22?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjB3ZWRkaW5nJTIwZGVjb3IlMjBmbG9yYWwlMjBuZXV0cmFsfGVufDB8fHx8MTc3NjM2MjkxMHww&ixlib=rb-4.1.0&q=85"
          alt="Wedding decor"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-moss/30 via-transparent to-terracotta/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 via-transparent to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-page">
          <div className="flex items-center gap-3 mb-3 opacity-90">
            <div className="h-px w-12 bg-ochre/70" />
            <span className="text-ochre text-lg font-serif">✦</span>
            <div className="h-px w-12 bg-ochre/70" />
          </div>
          <p className="font-serif italic text-2xl xl:text-3xl font-light leading-snug max-w-md drop-shadow-md">
            Where every detail of your celebration finally comes together.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16 relative">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-12">
            <Flower className="w-8 h-8 text-terracotta" strokeWidth={1.5} />
            <span className="font-brand text-2xl text-charcoal">Shaadi Planner</span>
          </div>

          <h1 className="font-serif font-light text-4xl sm:text-5xl text-charcoal tracking-tight mb-4">
            Begin your journey
          </h1>
          <p className="text-muted font-light mb-10">
            Create an account to start planning your dream wedding.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-4 bg-terracotta/10 border border-terracotta/30 rounded-sm text-terracotta text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name" className="text-charcoal font-sans text-sm uppercase tracking-wider">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-field h-12"
                placeholder="Your name"
                required
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-charcoal font-sans text-sm uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field h-12"
                placeholder="your@email.com"
                required
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-charcoal font-sans text-sm uppercase tracking-wider">Password</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field h-12"
                placeholder="••••••••"
                required
                data-testid="register-password-input"
              />
              <PasswordStrengthMeter password={password} data-testid="register-password-strength" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-charcoal font-sans text-sm uppercase tracking-wider">Confirm Password</Label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-field h-12"
                placeholder="••••••••"
                required
                data-testid="register-confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full h-12"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <p className="mt-8 text-center text-muted">
            Already have an account?{' '}
            <Link to="/login" className="text-terracotta hover:text-terracotta-dark transition-colors" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
